
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Check, Eye, Mail, Calendar, BarChart3 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface MetricProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  description?: string;
  progress?: number;
  className?: string;
}

const MetricCard: React.FC<MetricProps> = ({ 
  title, 
  value, 
  icon, 
  description, 
  progress, 
  className = "" 
}) => {
  return (
    <Card className={`overflow-hidden ${className}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="rounded-full bg-muted p-2">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
        {progress !== undefined && (
          <Progress value={progress} className="h-2 mt-2" />
        )}
      </CardContent>
    </Card>
  );
};

interface PartnerDashboardMetricsProps {
  totalLeads: number;
  closedDeals: number;
  successRate: number;
  viewedSMS: number;
  sentSMS: number;
  viewRate: number;
  emailsSent?: number;
  emailsOpened?: number;
  emailOpenRate?: number;
  leadsLastWeek?: number;
  leadsLastMonth?: number;
}

const PartnerDashboardMetrics: React.FC<PartnerDashboardMetricsProps> = ({
  totalLeads,
  closedDeals,
  successRate,
  viewedSMS,
  sentSMS,
  viewRate,
  emailsSent = 0,
  emailsOpened = 0,
  emailOpenRate = 0,
  leadsLastWeek = 0,
  leadsLastMonth = 0,
}) => {
  const opportunityInputs = emailsSent > 0
    ? [successRate, viewRate, emailOpenRate]
    : [successRate, viewRate];

  const opportunityScore = Math.round(
    opportunityInputs.reduce((sum, value) => sum + value, 0) / opportunityInputs.length
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <MetricCard
        title="Συνολικά Leads"
        value={totalLeads}
        icon={<Users className="h-4 w-4 text-blue-500" />}
        description={leadsLastMonth > 0 ? `+${leadsLastMonth} leads τον τελευταίο μήνα` : "Χωρίς νέα leads τον τελευταίο μήνα"}
        className="bg-blue-50/50"
      />
      
      <MetricCard
        title="Κλεισμένες Συμφωνίες"
        value={closedDeals}
        icon={<Check className="h-4 w-4 text-green-500" />}
        description={`${successRate}% ποσοστό επιτυχίας`}
        progress={successRate}
        className="bg-green-50/50"
      />
      
      <MetricCard
        title="Ποσοστό Προβολής SMS"
        value={`${viewRate}%`}
        icon={<Eye className="h-4 w-4 text-purple-500" />}
        description={`${viewedSMS} από ${sentSMS} απεσταλμένα SMS`}
        progress={viewRate}
        className="bg-purple-50/50"
      />
      
      {emailsSent > 0 && (
        <MetricCard
          title="Ποσοστό Ανοίγματος Email"
          value={`${emailOpenRate.toFixed(1)}%`}
          icon={<Mail className="h-4 w-4 text-amber-500" />}
          description={`${emailsOpened} από ${emailsSent} απεσταλμένα email`}
          progress={emailOpenRate}
          className="bg-amber-50/50"
        />
      )}
      
      <MetricCard
        title="Πρόσφατη Δραστηριότητα"
        value={leadsLastWeek}
        icon={<Calendar className="h-4 w-4 text-indigo-500" />}
        description="Νέα leads τις τελευταίες 7 ημέρες"
        className="bg-indigo-50/50"
      />
      
      <MetricCard
        title="Δείκτης Ευκαιρίας"
        value={opportunityScore}
        icon={<BarChart3 className="h-4 w-4 text-rose-500" />}
        description="Συνδυασμός μετατροπής και αλληλεπίδρασης"
        progress={opportunityScore}
        className="bg-rose-50/50"
      />
    </div>
  );
};

export default PartnerDashboardMetrics;
